'use client'

import {useState} from 'react'
import {Loader2, Volume2} from 'lucide-react'
import {apiPost} from '@/lib/api'
import {message} from '@/components/Toast'

/**
 * Inline TTS button for composers. Calls `/api/tts/synthesize`,
 * decodes the base64 payload into a File, and hands it back via
 * `onAttachVoice` so the host composer can treat it like a recorded
 * voice memo.
 *
 * The button is intentionally tiny — only the lucide `Volume2` icon, no
 * label — to fit next to existing compose-bar icons.
 */
type Props = {
    getText: () => string
    voiceId?: string
    onAttachVoice: (payload: {
        blob: Blob
        file: File
        previewUrl: string
        durationSeconds: number
    }) => void
    /** Extra class tweaks from the parent (color, size, etc.). */
    className?: string
    disabled?: boolean
    title?: string
}

type SynthesizeResponse = {
    success: boolean
    base64?: string
    mimeType?: string
    format?: 'wav' | 'mp3'
    playLength?: number
    message?: string
}

function dataUrlToBlob(dataUrl: string, mimeType: string): Blob | null {
    const comma = dataUrl.indexOf(',')
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
    try {
        const binary = atob(b64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        return new Blob([bytes], {type: mimeType})
    } catch {
        return null
    }
}

export function TtsButton({
    getText,
    voiceId,
    onAttachVoice,
    className,
    disabled,
    title = '文本转语音',
}: Props) {
    const [busy, setBusy] = useState(false)

    async function handleClick() {
        if (busy || disabled) return
        const text = getText().trim()
        if (!text) {
            message.warning('请先输入文字')
            return
        }
        setBusy(true)
        try {
            const response = await apiPost<SynthesizeResponse>('/tts/synthesize', {
                text,
                voice: voiceId,
            })
            if (!response.success || !response.base64) {
                message.error(response.message || '合成失败')
                return
            }
            const mimeType = response.mimeType || 'audio/wav'
            const blob = dataUrlToBlob(response.base64, mimeType)
            if (!blob) {
                message.error('语音解码失败')
                return
            }
            const ext = response.format === 'mp3' ? 'mp3' : 'wav'
            const file = new File([blob], `tts-${Date.now()}.${ext}`, {type: mimeType})
            const previewUrl = URL.createObjectURL(blob)
            onAttachVoice({
                blob,
                file,
                previewUrl,
                durationSeconds: response.playLength || 1,
            })
        } catch {
            /* toast fired by api.ts */
        } finally {
            setBusy(false)
        }
    }

    return (
        <button
            type="button"
            title={title}
            onClick={handleClick}
            disabled={busy || disabled}
            className={
                className ??
                'cursor-pointer hover:text-accent transition-colors disabled:opacity-50 disabled:cursor-wait'
            }
        >
            {busy ? <Loader2 size={20} className="animate-spin"/> : <Volume2 size={20}/>}
        </button>
    )
}
